import { clsx } from 'clsx';

interface DotGridProps {
  className?: string;
  /** Mask intensity: how much center is hidden (0-1). Default: 0.3 */
  maskIntensity?: number;
  /** Dot opacity (0-1). Default: 1 */
  opacity?: number;
  /** Fade out at bottom for smooth transition to next section */
  fadeBottom?: boolean;
}

/**
 * Subtle dot grid background pattern with elliptical mask.
 * Dots are less visible near center (masked) and more visible towards edges.
 */
export function DotGrid({
  className,
  maskIntensity = 0.3,
  opacity = 1,
  fadeBottom = false,
}: DotGridProps) {
  const transparentStop = Math.round(maskIntensity * 100);

  // Ellipse mask for center
  const ellipseMask = `radial-gradient(ellipse 70% 50% at 50% 50%, transparent 0%, transparent ${transparentStop}%, black 100%)`;

  // Bottom fade mask
  const bottomFadeMask = 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)';

  // Combine masks if fadeBottom is true
  const combinedMask = fadeBottom ? `${ellipseMask}, ${bottomFadeMask}` : ellipseMask;

  return (
    <div
      className={clsx('absolute inset-0 pointer-events-none -z-10', className)}
      aria-hidden="true"
    >
      <div className="h-full max-w-[1440px] mx-auto px-6 md:px-16 xl:px-[180px]">
        <div
          className="h-full w-full"
          style={
            {
              opacity,
              backgroundImage: `radial-gradient(var(--dot-color) var(--dot-size), transparent var(--dot-size))`,
              backgroundSize: 'var(--dot-space) var(--dot-space)',
              backgroundPosition: '0 0',
              maskImage: combinedMask,
              WebkitMaskImage: combinedMask,
              maskComposite: fadeBottom ? 'intersect' : undefined,
              WebkitMaskComposite: fadeBottom ? 'source-in' : undefined,
            } as React.CSSProperties
          }
        />
      </div>
    </div>
  );
}
